import { createContext, useContext } from 'react';
import { type StoreApi, createStore, useStore } from 'zustand';
import { createSignOffSlice, signOffInitialState } from './slices/sign-off';
import { createUiSlice, uiInitialState } from './slices/ui';
import type { LetterSenderState, LetterSenderStore } from './types';

export const LetterSenderStoreContext = createContext<StoreApi<LetterSenderStore> | null>(
  null
);

const initialState: LetterSenderState = {
  ...signOffInitialState,
  ...uiInitialState,
};

const storeCache = new Map<string, StoreApi<LetterSenderStore>>();

export const createLetterSenderStore = () =>
  createStore<LetterSenderStore>()((set, get, store) => {
    const sliceArgs = [set, get, store] as const;

    return {
      ...initialState,
      ...createSignOffSlice(...sliceArgs),
      ...createUiSlice(...sliceArgs),
      reset: () => set(() => ({ ...initialState })),
    };
  });

export const getOrCreateLetterSenderStore = (letterId: string) => {
  const cached = storeCache.get(letterId);
  if (cached) return cached;

  const newStore = createLetterSenderStore();
  storeCache.set(letterId, newStore);
  return newStore;
};

/**
 * Evicts all cached letter stores, calling reset() on each.
 * Call on session change to prevent cross-session state bleed.
 */
export const evictAllLetterStores = () => {
  for (const store of storeCache.values()) {
    store.getState().reset();
  }
  storeCache.clear();
};

export const useLetterSenderStore = <T>(selector: (state: LetterSenderStore) => T): T => {
  const store = useContext(LetterSenderStoreContext);
  if (!store) {
    throw new Error('Missing LetterSenderStoreProvider');
  }
  return useStore(store, selector);
};
